// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — CopilotButton (V·IA Copilot inline)
// Sparkle FAB that appears on hover over document h1/h2 sections.
// Opens a popover with 4 quick actions + streaming response.
// ═══════════════════════════════════════════════════════════════

import { useState, useRef, useEffect, useCallback, type CSSProperties } from 'react';
import { Ic, ICON_PATHS } from '../../../lib/ui';
import { C } from '../../../lib/design';
import { useI18n } from '../../../i18n';
import { useStreamChat } from './hooks/useStreamChat';

// ─── Types ───

interface CopilotButtonProps {
  codigoDocumento: string;
  seccionId?: string;
  seccionTexto?: string;
  position: { top: number; right: number };
  onApply?: (text: string) => void;
}

type CopilotAction = 'completar' | 'corregir' | 'sugerir' | 'verificar';

interface ActionDef {
  key: CopilotAction;
  icon: keyof typeof ICON_PATHS;
  prompt: (codigo: string, seccion: string) => string;
}

const ACTIONS: ActionDef[] = [
  {
    key: 'completar',
    icon: 'edit',
    prompt: (codigo, seccion) =>
      `Completa y desarrolla esta seccion del documento ${codigo}: "${seccion}". Genera contenido profesional y normativo.`,
  },
  {
    key: 'corregir',
    icon: 'check',
    prompt: (codigo, seccion) =>
      `Revisa y corrige esta seccion del documento ${codigo}: "${seccion}". Identifica errores ortograficos, gramaticales y de estilo.`,
  },
  {
    key: 'sugerir',
    icon: 'send',
    prompt: (codigo, seccion) =>
      `Sugiere mejoras para esta seccion del documento ${codigo}: "${seccion}". Proporciona recomendaciones concretas.`,
  },
  {
    key: 'verificar',
    icon: 'doc',
    prompt: (codigo, seccion) =>
      `Verifica la conformidad normativa de esta seccion del documento ${codigo}: "${seccion}". Identifica si cumple los requisitos del sistema de gestion.`,
  },
];

// ─── Component ───

export function CopilotButton({
  codigoDocumento,
  seccionId,
  seccionTexto = '',
  position,
  onApply,
}: CopilotButtonProps) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [activeAction, setActiveAction] = useState<CopilotAction | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const { respuestaParcial, isStreaming, isThinking, error, enviar, cancelar } = useStreamChat({
    contexto: { modulo: 'document', vista: 'copilot', entidadId: codigoDocumento },
  });

  const handleDiscard = useCallback(() => {
    cancelar();
    setOpen(false);
    setActiveAction(null);
  }, [cancelar]);

  const handleAction = useCallback(
    (action: CopilotAction) => {
      setActiveAction(action);
      const def = ACTIONS.find((a) => a.key === action);
      if (!def) return;
      const seccion = seccionTexto || seccionId || 'seccion actual';
      enviar(def.prompt(codigoDocumento, seccion));
    },
    [codigoDocumento, seccionId, seccionTexto, enviar],
  );

  const handleCopy = useCallback(async () => {
    if (respuestaParcial) {
      await navigator.clipboard.writeText(respuestaParcial);
    }
  }, [respuestaParcial]);

  const handleApply = useCallback(() => {
    onApply?.(respuestaParcial);
    handleDiscard();
  }, [respuestaParcial, onApply, handleDiscard]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        handleDiscard();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, handleDiscard]);

  // ─── Styles ───

  const fabStyle: CSSProperties = {
    position: 'absolute',
    top: position.top,
    right: position.right,
    width: 28,
    height: 28,
    borderRadius: '50%',
    border: 'none',
    background: `linear-gradient(135deg, ${C.azul2}, ${C.celeste})`,
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(31,56,100,0.25)',
    transition: 'transform 0.2s, box-shadow 0.2s',
    zIndex: 20,
  };

  const popoverStyle: CSSProperties = {
    position: 'absolute',
    top: position.top + 36,
    right: position.right,
    width: 380,
    maxWidth: 'calc(100vw - 40px)',
    background: '#fff',
    borderRadius: 10,
    boxShadow: '0 8px 32px rgba(31,56,100,0.18)',
    border: `1px solid ${C.azul2}22`,
    zIndex: 30,
    overflow: 'hidden',
  };

  const actionBtnStyle = (isActive: boolean): CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    border: 'none',
    borderRadius: 6,
    background: isActive ? `${C.azul2}15` : 'transparent',
    color: C.azul1,
    cursor: 'pointer',
    fontSize: 13,
    fontFamily: 'Montserrat, sans-serif',
    width: '100%',
    textAlign: 'left' as const,
    transition: 'background 0.15s',
  });

  const responseStyle: CSSProperties = {
    padding: '12px 16px',
    maxHeight: 300,
    overflowY: 'auto',
    fontSize: 13,
    lineHeight: 1.6,
    color: '#374151',
    fontFamily: 'Montserrat, sans-serif',
    whiteSpace: 'pre-wrap',
    borderTop: `1px solid ${C.azul2}15`,
  };

  const footerStyle: CSSProperties = {
    display: 'flex',
    gap: 8,
    padding: '8px 16px 12px',
    borderTop: `1px solid ${C.azul2}10`,
    justifyContent: 'flex-end',
  };

  const smallBtnStyle = (variant: 'primary' | 'secondary' | 'ghost'): CSSProperties => ({
    padding: '5px 14px',
    borderRadius: 6,
    border: variant === 'ghost' ? `1px solid ${C.azul2}30` : 'none',
    background:
      variant === 'primary' ? C.azul2 : variant === 'secondary' ? `${C.azul2}10` : 'transparent',
    color: variant === 'primary' ? '#fff' : C.azul1,
    fontSize: 12,
    fontFamily: 'Montserrat, sans-serif',
    cursor: 'pointer',
    fontWeight: 500,
  });

  return (
    <>
      {/* Sparkle FAB */}
      <button
        ref={buttonRef}
        style={fabStyle}
        onClick={() => setOpen((v) => !v)}
        title={t.via.copilot.titulo}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.transform = 'scale(1.15)';
          (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 16px rgba(31,56,100,0.35)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
          (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(31,56,100,0.25)';
        }}
      >
        <Ic d={ICON_PATHS.sparkle} s={16} />
      </button>

      {/* Popover */}
      {open && (
        <div ref={popoverRef} style={popoverStyle}>
          {/* Header */}
          <div
            style={{
              padding: '10px 16px',
              borderBottom: `1px solid ${C.azul2}15`,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Ic d={ICON_PATHS.sparkle} s={16} c={C.azul2} />
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: C.azul1,
                fontFamily: 'Montserrat, sans-serif',
              }}
            >
              {t.via.copilot.titulo}
            </span>
            {seccionTexto && (
              <span
                style={{
                  fontSize: 11,
                  color: '#6b7280',
                  marginLeft: 'auto',
                  maxWidth: 180,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {seccionTexto}
              </span>
            )}
          </div>

          {/* Actions */}
          {!activeAction && (
            <div style={{ padding: '8px' }}>
              {ACTIONS.map((action) => (
                <button
                  key={action.key}
                  style={actionBtnStyle(false)}
                  onClick={() => handleAction(action.key)}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.background = `${C.azul2}10`;
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.background = 'transparent';
                  }}
                >
                  <Ic d={ICON_PATHS[action.icon]} s={16} c={C.azul2} />
                  {t.via.copilot[action.key]}
                </button>
              ))}
            </div>
          )}

          {/* Streaming response */}
          {activeAction && (
            <>
              {/* Active action label */}
              <div style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Ic
                  d={ICON_PATHS[(ACTIONS.find((a) => a.key === activeAction) ?? ACTIONS[0]).icon]}
                  s={14}
                  c={C.azul2}
                />
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: C.azul2,
                    fontFamily: 'Montserrat, sans-serif',
                  }}
                >
                  {t.via.copilot[activeAction]}
                </span>
                {(isStreaming || isThinking) && (
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: C.celeste,
                      animation: 'copilot-pulse 1s infinite',
                      marginLeft: 4,
                    }}
                  />
                )}
              </div>

              {/* Response area */}
              <div style={responseStyle}>
                {isThinking && !respuestaParcial && (
                  <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>
                    {t.via.chat.procesando}
                  </span>
                )}
                {respuestaParcial && respuestaParcial}
                {error && <span style={{ color: '#ef4444' }}>{error}</span>}
                {isStreaming && <span style={{ opacity: 0.5 }}>|</span>}
              </div>

              {/* Footer buttons */}
              {(respuestaParcial || error) && !isStreaming && (
                <div style={footerStyle}>
                  <button style={smallBtnStyle('ghost')} onClick={handleDiscard}>
                    {t.via.copilot.descartar}
                  </button>
                  <button style={smallBtnStyle('secondary')} onClick={handleCopy}>
                    {t.via.copilot.copiar}
                  </button>
                  {onApply && respuestaParcial && (
                    <button style={smallBtnStyle('primary')} onClick={handleApply}>
                      {t.via.copilot.aplicar}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Pulse animation */}
      <style>{`
        @keyframes copilot-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </>
  );
}
